import { Component, computed, inject, input, viewChild } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslocoService } from '@jsverse/transloco';
import { take } from 'rxjs/operators';
import { Room } from '../../../core/models/entities/room';
import { RoomPermission } from '../../../core/models/entities/permission';
import { BookableByHumanized } from '../../../core/models/common/bookable-by';
import {
  ROOM_COLORS,
  RoomColor,
} from '../../../core/helpers/room-color.helper';
import { CalendarRoomsService } from '../../../core/services/calendar-rooms.service';
import { ModalService } from '../../../core/services/modal.service';
import { RoomsService } from '../../../core/services/rooms.service';
import { ToastService } from '../../../core/services/toast.service';
import { fromStore } from '../../../core/store';
import { roomsActions } from '../../../core/store/rooms';
import { DropdownComponent } from '../../../shared/components/dropdown/dropdown.component';
import { RoomICalSyncModalComponent } from '../../../shared/components/room-ical-sync-modal/room-ical-sync-modal.component';
import { RoomDetailsModalComponent } from '../room-details-modal/room-details-modal.component';

@Component({
  selector: 'app-calendar-room-menu',
  template: `
    <app-dropdown>
      <button
        dropdownButton
        type="button"
        class="flex size-7 cursor-pointer items-center justify-center rounded-full text-gray-500 hover:bg-gray-200 hover:text-gray-800"
        [attr.aria-label]="'bookings.calendar-room.menu-label' | transloco"
      >
        <i class="far fa-ellipsis-vertical"></i>
      </button>

      <button dropdownAction type="button" (click)="openICalSync()">
        {{ 'bookings.calendar-room.menu-subscribe' | transloco }}
      </button>
      <button dropdownAction type="button" (click)="openRoomDetails()">
        {{ 'bookings.calendar-room.menu-room-details' | transloco }}
      </button>

      <ng-container ngProjectAs="[dropdownAction]">
        <ng-template [appHasPermission]="RoomPermission.Update">
          <div class="my-2 border-t border-gray-200"></div>
          <div class="px-3 pt-1 pb-2">
            <div class="mb-2 text-xs text-gray-500">
              {{ 'bookings.calendar-room.menu-color-label' | transloco }}
            </div>
            <div class="grid grid-cols-6 gap-2">
              @for (color of roomColors; track color.name) {
                <button
                  type="button"
                  class="flex size-6 cursor-pointer items-center justify-center rounded-full text-[10px] text-white transition-transform hover:scale-110"
                  [ngClass]="color.dot"
                  [attr.aria-label]="color.name"
                  (click)="selectColor(color)"
                >
                  @if (color.name === currentColor().name) {
                    <i class="far fa-check"></i>
                  }
                </button>
              }
            </div>
          </div>
        </ng-template>
      </ng-container>
    </app-dropdown>
  `,
  host: {
    '(click)': '$event.stopPropagation()',
    '[class.!opacity-100]': 'dropdown()?.isOpen()',
  },
  standalone: false,
})
export class CalendarRoomMenuComponent {
  room = input.required<Room>();

  private store = inject(Store);
  private modalService = inject(ModalService);
  private roomsService = inject(RoomsService);
  private toastService = inject(ToastService);
  private translocoService = inject(TranslocoService);
  private calendarRooms = inject(CalendarRoomsService);
  protected dropdown = viewChild(DropdownComponent);

  readonly roomColors = ROOM_COLORS;
  currentColor = computed(() => this.calendarRooms.colorOf(this.room()));

  openICalSync() {
    const instance = this.modalService.open(RoomICalSyncModalComponent, {
      title: this.translocoService.translate(
        'bookings.calendar-room.ical-sync-modal-title',
      ),
      showActionButtons: false,
      size: 'lg',
    });
    const component = instance.componentRef
      .instance as RoomICalSyncModalComponent;
    component.setData(this.room());
  }

  openRoomDetails() {
    const room = this.room();
    this.store
      .select(fromStore.selectAuthVendor)
      .pipe(take(1))
      .subscribe((vendor) => {
        const instance = this.modalService.open(RoomDetailsModalComponent, {
          title: `
            <div class="flex items-center space-x-4">
              <span>${this.translocoService.translate('bookings.calendar-room.room-preview-label')} : ${room.full_name}</span>
              <div class="inline-block rounded-full font-bold bg-secondary-900 text-white px-5 py-1 text-sm">${this.translocoService.translate(
                BookableByHumanized[room.bookable_by],
              )}</div>
            </div>`,
          size: 'screen',
          showActionButtons: false,
        });
        const component = instance.componentRef
          .instance as RoomDetailsModalComponent;
        component.room = room;
        component.vendor = vendor;
      });
  }

  async selectColor(color: RoomColor) {
    this.dropdown()?.closeDropdown();
    try {
      const room = await this.roomsService.saveColor(
        this.room().id,
        color.name,
      );
      this.store.dispatch(roomsActions.updateRoom({ room }));
    } catch {
      this.toastService.error(
        this.translocoService.translate(
          'bookings.calendar-room.color-save-error',
        ),
      );
    }
  }

  protected readonly RoomPermission = RoomPermission;
}
